import type { ComebackView } from '@aj/core';
import { Card, HStack, Icon, Text, VStack, cx } from '@aj/ui';

import { useT } from '../../providers/I18nProvider.js';

export type ComebackCardProps = {
  comeback: ComebackView;
  className?: string;
  padding?: 4 | 5;
};

/**
 * The return (§45, §48 #3).
 *
 * This card is the single strongest evidence-backed element in the product:
 * rewarding the day *after* a miss was the top-ranked intervention of 54 in a
 * 61,293-person megastudy. Everything about how it is written follows from
 * that one finding.
 *
 * What it deliberately does NOT do:
 *
 *  • It never names the streak that broke. "You lost your 12-day streak" is a
 *    loss notice, and loss notices at the moment of return are precisely how
 *    the 70%-abandonment-in-100-days curve is produced.
 *  • It never asks why. The app asks what you said you would do, not how you
 *    feel about it — that is the first operating principle in §45.
 *  • It carries no action of its own. The habit rows are directly beneath it,
 *    each with its floor one tap away, so the invitation resolves into the
 *    cheapest possible act rather than into another screen.
 *
 * It appears from the first moment of the day, before anything is done, and
 * stays after the return is made — the second state is the reward, and cutting
 * it the instant the user acts would delete the payoff.
 */
export function ComebackCard({ comeback, className, padding = 4 }: ComebackCardProps) {
  const t = useT();
  const { made, missedDays } = comeback;

  // Tinted only once the return is made: before that it is an invitation and
  // should sit quietly; after, it is the payoff and should not.
  return (
    <Card padding={padding} className={cx(made && 'border-accent/30 bg-accent-soft', className)}>
      <VStack gap={2}>
        <HStack gap={2} align="center">
          <Icon name={made ? 'check' : 'bolt'} size={18} className="shrink-0 text-accent" />
          <Text variant="label" tone="ink">
            {t(made ? 'comeback.madeTitle' : 'comeback.title')}
          </Text>
        </HStack>

        <Text variant="body" tone="muted">
          {made
            ? t('comeback.madeBody')
            : `${t('comeback.missed', { count: missedDays })} ${t('comeback.invite')}`}
        </Text>
      </VStack>
    </Card>
  );
}
